import { useCallback, useEffect } from 'react';

import { updateMessageById } from 'containers/admin/clients/client/tabs-components/ai-assistant/helpers/message-helpers';

import { INPUT_PART_STORAGE_KEY_PREFIX } from 'containers/admin/clients/client/tabs-components/ai-assistant/ai-assistant.constants';

import type { ChatMessage } from 'containers/admin/clients/client/tabs-components/ai-assistant/hooks/use-active-input-part';

const getStorageKey = (conversationId: string): string =>
  `${INPUT_PART_STORAGE_KEY_PREFIX}-${conversationId}`;

type SetMessagesForConversation = (
  conversationId: string,
  updater: (messages: ChatMessage[]) => ChatMessage[]
) => void;

interface UseInputPartLifecycleArgs {
  activeMessage: ChatMessage | null;
  conversationId: string | null;
  messages: ChatMessage[];
  setMessagesForConversation: SetMessagesForConversation;
  sendMessage: (message: string) => void;
}

interface UseInputPartLifecycleResult {
  dismiss: () => void;
  submitAndSend: (formattedText: string) => void;
}

/**
 * Generic lifecycle operations shared by every input-context message part.
 * Also persists the active message's parts to localStorage and restores them
 * when the conversation loads from the server.
 *
 *   - `dismiss()`                    — flag the active message so the input
 *                                      part stops rendering, and clear the
 *                                      persisted copy from localStorage.
 *   - `submitAndSend(formattedText)` — dismiss, then send the formatted text
 *                                      as a new user message.
 */
const useInputPartLifecycle = ({
  activeMessage,
  conversationId,
  messages,
  setMessagesForConversation,
  sendMessage,
}: UseInputPartLifecycleArgs): UseInputPartLifecycleResult => {
  useEffect(() => {
    if (!conversationId || !activeMessage) return;

    localStorage.setItem(
      getStorageKey(conversationId),
      JSON.stringify({
        messageId: activeMessage.id,
        parts: activeMessage.parts,
      })
    );
  }, [conversationId, activeMessage?.id, activeMessage?.parts]);

  useEffect(() => {
    if (!conversationId || !messages.length) return;

    const storageKey = getStorageKey(conversationId);
    const stored = localStorage.getItem(storageKey);

    if (!stored) return;

    try {
      const { messageId, parts } = JSON.parse(stored) as {
        messageId: string;
        parts: ChatMessage['parts'];
      };
      const targetMessage = messages.find(
        (message) => message.id === messageId
      );

      if (targetMessage && !targetMessage.parts?.length) {
        setMessagesForConversation(conversationId, (previousMessages) =>
          updateMessageById(previousMessages, messageId, { parts })
        );
      } else if (!targetMessage) {
        localStorage.removeItem(storageKey);
      }
    } catch {
      localStorage.removeItem(storageKey);
    }
  }, [conversationId, messages.length, setMessagesForConversation]);

  const dismiss = useCallback(() => {
    if (!activeMessage || !conversationId) return;

    setMessagesForConversation(conversationId, (previousMessages) =>
      updateMessageById(previousMessages, activeMessage.id, {
        inputPartDismissed: true,
      })
    );

    localStorage.removeItem(getStorageKey(conversationId));
  }, [activeMessage, conversationId, setMessagesForConversation]);

  const submitAndSend = useCallback(
    (formattedText: string) => {
      dismiss();
      sendMessage(formattedText);
    },
    [dismiss, sendMessage]
  );

  return { dismiss, submitAndSend };
};

export default useInputPartLifecycle;
